"use client";

import { ArrowUp, Paperclip } from "lucide-react";
import type { FormEvent, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/**
 * The prompt composer as markup: attached files, the text field, then a
 * footer with attach, voice, model, sources and any extra actions, and the
 * send button. Every control is a slot or a prop, so SessionStartComposer
 * renders it with live controls and a fixture renders the same frame with its
 * own.
 */
export function SessionStartComposerView(props: {
  className?: string;
  /** Floats above the composer, e.g. the dictation terminal. */
  overlay?: ReactNode;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** The files attached to the prompt, above the text field (AttachmentCardRow). */
  attachments?: ReactNode;
  /** The text field, usually a PromptChannelMentionInput. */
  textarea: ReactNode;
  error?: string | null;
  /** The file input and ComposerAttachButton, when attaching is offered. */
  attach?: ReactNode;
  dictation?: ReactNode;
  model?: ReactNode;
  /** The sources the Twin may read (ComposerSources). */
  sources?: ReactNode;
  footerActions?: ReactNode;
  submitDisabled: boolean;
  submitting: boolean;
  submitAriaLabel: string;
}) {
  return (
    <div
      data-tour="composer"
      className={cn("relative w-full max-w-[800px]", props.className)}
    >
      {props.overlay}
      <form
        onSubmit={props.onSubmit}
        className="mlx-prompt-composer chat-composer-shadow w-full overflow-visible rounded-xl border border-transparent bg-control"
      >
        {props.attachments}
        {props.textarea}
        <div className="mlx-prompt-footer">
          <div className="flex min-w-0 items-center gap-1">
            {props.error ? (
              <div
                className="flex min-w-0 items-center gap-2 truncate text-xs text-destructive"
                aria-live="polite"
              >
                <span className="truncate">{props.error}</span>
              </div>
            ) : null}
            {props.attach}
            {props.dictation}
            {props.model}
            {props.sources}
            {props.footerActions}
          </div>
          <Button
            type="submit"
            size="icon"
            disabled={props.submitDisabled}
            className="size-8 shrink-0 rounded-full"
            aria-label={props.submitting ? "Starting session" : props.submitAriaLabel}
          >
            {props.submitting ? (
              <Spinner aria-hidden />
            ) : (
              <ArrowUp className="size-4" aria-hidden />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** The composer's attach control. */
export function ComposerAttachButton(props: {
  disabled: boolean;
  uploading: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="size-8 rounded-md text-muted-foreground hover:text-foreground"
      disabled={props.disabled}
      title="Attach file"
      aria-label={props.uploading ? "Uploading file" : "Attach file"}
      onClick={props.onClick}
    >
      {props.uploading ? (
        <Spinner aria-hidden />
      ) : (
        <Paperclip className="size-4" aria-hidden />
      )}
    </Button>
  );
}
